import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Autocomplete,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Client } from '../../../utils/types';
import { fetchClients, updateClient } from '../../../utils/helper';

import { useAuth } from "../../../context/AuthContext";

interface UpdateGraphModalProps {
  open: boolean;
  onClose: () => void;
}

export const UpdateGraphModal: React.FC<UpdateGraphModalProps> = ({ open, onClose }) => {
  const { jwt } = useAuth();
  
  const queryClient = useQueryClient();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [revenue, setRevenue] = useState<string[]>(new Array(12).fill(''));

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Fetch clients for autocomplete
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => fetchClients(jwt),
  });

  // Populate revenue fields when a client is selected
  useEffect(() => {
    if (selectedClient?.revenue) {
      setRevenue(selectedClient.revenue.map(val => val.toString()));
    } else {
      setRevenue(new Array(12).fill(''));
    }
  }, [selectedClient]);

  const updateClientMutation = useMutation({
    mutationFn: ({ clientId, client }: { clientId: string; client: Partial<Client> }) =>
      updateClient(clientId, client, jwt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setSelectedClient(null);
      setRevenue(new Array(12).fill(''));
      onClose();
    },
  });

  const handleRevenueChange = (index: number, value: string) => {
    const newRevenue = [...revenue];
    newRevenue[index] = value;
    setRevenue(newRevenue);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedClient?.id) {
      const revenueNumbers = revenue.map(val => (val ? Number(val) : 0));
      updateClientMutation.mutate({
        clientId: selectedClient.id,
        client: { revenue: revenueNumbers },
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="update-revenue-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography id="update-revenue-modal-title" variant="h6" component="h2">
            Update Client Revenue
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={clients}
                getOptionLabel={(client) => `${client.clientName} (${client.clientEmail})`}
                value={selectedClient}
                onChange={(event, newValue) => setSelectedClient(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Client"
                    variant="outlined"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: null,
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>
            {selectedClient && (
              <Grid container spacing={2} size={{ xs: 12 }}>
                {revenue.map((value, index) => (
                  <Grid size={{ xs: 3 }} key={index}>
                    <TextField
                      fullWidth
                      label={months[index]}
                      type="number"
                      value={value}
                      onChange={(e) => handleRevenueChange(index, e.target.value)}
                      variant="outlined"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedClient
                  ? `Update revenue for ${selectedClient.clientName}. Enter values for each month.`
                  : 'Select a client to update their revenue.'}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!selectedClient}
            >
              Update
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
